import { createMuiTheme, lighten } from '@material-ui/core/styles';
import { LabComponentNameToClassKey } from '@material-ui/lab/themeAugmentation';
import { colors, typography } from '@hydra/common/styles';
import { values as customBreakpoints, print } from '@hydra/common/styles/breakpoints';

type PaletteColor = {
  type: 'dark' | 'light'
}

declare module '@material-ui/core/styles/createPalette' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type CustomPaletteColors = {
    hover: string;
    darker: string;
    darkest: string;
    main: string;
    smoke: string;
    light: string;
    medium: string;
    dark: string;
    lighter: string;
  };

  interface PaletteColor extends CustomPaletteColors {}

  interface TypeBackground {
    secondary: string
  }

  interface Palette {
    primary: PaletteColor
    mango: PaletteColor
    amethyst: PaletteColor
    competitor: PaletteColor
  }
}

declare module '@material-ui/core/styles/createBreakpoints' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Breakpoints {
    print: () => string
  }
  interface BreakpointOverrides {
    pdfPortrait: true; // adds the `pdfPortrait` breakpoint
  }
}

declare module '@material-ui/core/styles/overrides' {
  interface ComponentNameToClassKey extends LabComponentNameToClassKey {}
}

const defaultTheme = createMuiTheme();

const fontSizes = {
  fontSize: typography.fontSize.medium,
  fontSizeTiny: typography.fontSize.xsmall,
  fontSizeSmall: typography.fontSize.small,
  fontSizeMedium: typography.fontSize.medium,
  fontSizeBase: typography.fontSize.base,
  fontSizeRoot: typography.fontSize.root,
  fontSizeLarge: typography.fontSize.large,
  fontSizeXLarge: typography.fontSize.xlarge,
  fontSizeXXLarge: typography.fontSize.xxlarge
};

const fontWeights = {
  fontWeightLight: typography.fontWeight.light,
  fontWeightRegular: typography.fontWeight.regular,
  fontWeightSemibold: typography.fontWeight.semibold,
  fontWeightBold: typography.fontWeight.bold
};

type CustomFontSizes = typeof fontSizes
type CustomFontWeights = typeof fontWeights

declare module '@material-ui/core/styles/createTypography' {
  interface TypographyOptions {
    fontWeightSemibold: React.CSSProperties['fontWeight'],
    h7?: TypographyStyleOptions,
    body3?: TypographyStyleOptions,
    buttonLarge?: TypographyStyleOptions,
    buttonMedium: TypographyStyleOptions,
    buttonSmall: TypographyStyleOptions,
    buttonNav?: TypographyStyleOptions
  }

  interface Typography {
    h7?: TypographyOptions,
    body3?: TypographyStyleOptions,
    buttonLarge?: TypographyStyleOptions,
    buttonMedium: TypographyStyleOptions,
    buttonSmall?: TypographyStyleOptions,
    buttonNav?: TypographyStyleOptions
  }
  interface Typography extends CustomFontSizes, CustomFontWeights {}
}

const TYPE_PALETTE: PaletteColor = { type: 'light' };

const woorankLightPalette = {
  common: {
    ...colors.common
  },
  mango: {
    ...colors.mango
  },
  primary: {
    ...colors.primary
  },
  secondary: {
    ...colors.grey
  },
  grey: {
    ...colors.grey
  },
  success: {
    ...colors.success
  },
  error: {
    ...colors.error
  },
  warning: {
    ...colors.warning
  },
  amethyst: {
    ...colors.amethyst
  },
  action: {
    hover: colors.grey[100],
    disabled: colors.grey[300]
  },
  background: {
    default: colors.common.white,
    secondary: colors.grey[100]
  },
  divider: colors.grey[200],
  text: {
    ...defaultTheme.palette.text,
    primary: colors.grey[900],
    secondary: colors.grey[500]
  },
  shadow: colors.shadow,
  competitor: {
    ...colors.competitor
  },
  navy: {
    ...colors.navy
  },
  sky: {
    ...colors.sky
  },
  lemon: {
    ...colors.lemon
  }
};

const woorankDarkPalette = {
  ...woorankLightPalette,
  divider: colors.grey[900],
  text: {
    primary: colors.common.white,
    secondary: colors.grey[100]
  },
  background: {
    default: colors.navy.dark,
    paper: colors.navy.dark,
    secondary: colors.navy.main
  }
};

const woorankPalette = TYPE_PALETTE.type === 'dark' ? woorankDarkPalette : woorankLightPalette;

const styleguideBaselineOverrides = {
  '@global': {
    body: {
      paddingTop: 0,
      lineHeight: 1.6,
      overflowY: 'scroll',
      overflowX: 'hidden',
      minWidth: 320,
      textRendering: 'optimizeLegibility',
      WebkitFontSmoothing: 'antialiased'
    },
    ':root': {
      fontSize: 16
    },
    img: {
      verticalAlign: 'middle'
    },
    p: {
      margin: '0 0 12px'
    },
    strong: {
      fontWeight: fontWeights.fontWeightSemibold
    },
    b: {
      fontWeight: fontWeights.fontWeightSemibold
    },
    a: {
      color: colors.primary.main,
      textDecoration: 'none',
      '&:hover, &:active, &:focus': {
        textDecoration: 'underline'
      }
    },
    ul: {
      marginTop: 0,
      marginBottom: 12
    },
    ol: {
      marginTop: 0,
      marginBottom: 12
    },
    h1: {
      fontSize: 38,
      fontWeight: fontWeights.fontWeightRegular
    },
    h2: {
      fontSize: 32,
      fontWeight: fontWeights.fontWeightRegular
    },
    h3: {
      fontSize: 24,
      fontWeight: fontWeights.fontWeightRegular
    },
    h4: {
      fontSize: 18,
      fontWeight: fontWeights.fontWeightRegular
    },
    h5: {
      fontSize: 16,
      fontWeight: fontWeights.fontWeightRegular
    },
    h6: {
      fontSize: 12,
      fontWeight: fontWeights.fontWeightRegular
    }
  }
} as const;

const customTheme = createMuiTheme({
  typography: {
    htmlFontSize: 16,
    fontFamily: ['Open Sans', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    ...fontSizes,
    ...fontWeights,
    button: {
      textTransform: 'none'
    },
    h1: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(38),
      lineHeight: 1,
      letterSpacing: '-0.01562em'
    },
    h2: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(32),
      lineHeight: 1,
      letterSpacing: '-0.00833em'
    },
    h3: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(24),
      lineHeight: 1.2,
      letterSpacing: '0em'
    },
    h4: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(18),
      lineHeight: 1.17,
      letterSpacing: '0.00735em'
    },
    h5: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(16),
      lineHeight: 1.33,
      letterSpacing: '0em'
    },
    h6: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: defaultTheme.typography.pxToRem(12),
      lineHeight: 1.6,
      letterSpacing: '0.0075em'
    },
    subtitle1: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: '1rem',
      lineHeight: 1.75,
      letterSpacing: '0.00938em'
    },
    subtitle2: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.00714em'
    },
    body1: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em'
    },
    body2: {
      fontWeight: fontWeights.fontWeightRegular,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em'
    },
    buttonSmall: {
      fontWeight: typography.fontWeight.semibold,
      fontSize: defaultTheme.typography.pxToRem(typography.fontSize.buttonSmall),
      lineHeight: defaultTheme.typography.pxToRem(typography.lineHeight.buttonSmall),
      letterSpacing: defaultTheme.typography.pxToRem(typography.letterSpacing.buttonSmall)
    },
    buttonMedium: {
      fontWeight: typography.fontWeight.semibold,
      fontSize: defaultTheme.typography.pxToRem(typography.fontSize.buttonMedium),
      lineHeight: defaultTheme.typography.pxToRem(typography.lineHeight.buttonMedium),
      letterSpacing: defaultTheme.typography.pxToRem(typography.letterSpacing.buttonMedium)
    },
    buttonLarge: {
      fontWeight: typography.fontWeight.semibold,
      fontSize: defaultTheme.typography.pxToRem(typography.fontSize.buttonLarge),
      lineHeight: defaultTheme.typography.pxToRem(typography.lineHeight.buttonLarge),
      letterSpacing: defaultTheme.typography.pxToRem(typography.letterSpacing.buttonLarge)
    }
  },
  breakpoints: {
    values: {
      ...customBreakpoints
    },
    print
  },
  shape: {
    borderRadius: 2
  },
  palette: {
    ...woorankPalette
  },
  mixins: {
    toolbar: {
      minHeight: 60,
      paddingLeft: defaultTheme.spacing(2),
      paddingRight: defaultTheme.spacing(2),
      [defaultTheme.breakpoints.up(customBreakpoints.sm)]: {
        paddingLeft: defaultTheme.spacing(2),
        paddingRight: defaultTheme.spacing(2)
      }
    }
  },
  props: {
    MuiButtonBase: {
      disableRipple: true
    }
  }
});

customTheme.overrides = {
  MuiCssBaseline: styleguideBaselineOverrides,
  MuiAppBar: {
    colorPrimary: {
      backgroundColor: customTheme.palette.primary.darker
    }
  },
  MuiOutlinedInput: {
    multiline: {
      padding: customTheme.spacing(1.6875, 1.5)
    },
    input: {
      padding: customTheme.spacing(1.6875, 1.5),
      '&:hover': {
        cursor: 'pointer'
      }
    },
    root: {
      borderRadius: customTheme.shape.borderRadius * 2,
      '&$error': {
        borderColor: customTheme.palette.error.light
      },
      '&.Mui-focused': {
        boxShadow: customTheme.shadows[3]
      },
      '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: customTheme.palette.grey[300]
      }
    },
    adornedEnd: {
      background: customTheme.palette.background.paper
    }
  },
  MuiDialogTitle: {
    root: {
      padding: customTheme.spacing(2)
    }
  },
  MuiFormControlLabel: {
    label: {
      '&$disabled': { opacity: 0.62 }
    }
  },
  MuiDialogContent: {
    root: {
      padding: customTheme.spacing(2)
    }
  },
  MuiTooltip: {
    arrow: {
      color: customTheme.palette.text.primary
    },
    tooltip: {
      backgroundColor: customTheme.palette.grey[900],
      borderRadius: customTheme.shape.borderRadius * 2.5,
      padding: customTheme.spacing(1, 1.5),
      fontSize: typography.fontSize.medium,
      fontWeight: typography.fontWeight.bold
    }
  },
  MuiButton: { // MuiButton overrides are failsafes for instances where custom Button is not being used
    // size='medium'
    contained: {
      ...customTheme.typography.buttonMedium,
      height: customTheme.spacing(4.75), // 38px
      borderRadius: customTheme.shape.borderRadius * 2.5,
      padding: customTheme.spacing(1.5, 3), // 12px 24px
      textTransform: 'capitalize',
      boxShadow: 'none',
      '&:active': {
        textDecoration: 'none'
      },
      '&:focus': {
        textDecoration: 'none'
      },
      '&$disabled': {
        backgroundColor: customTheme.palette.grey[100],
        color: customTheme.palette.grey[400]
      }
    },
    // size='small'
    containedSizeSmall: {
      ...customTheme.typography.buttonSmall,
      height: customTheme.spacing(3.75), // 30px
      padding: customTheme.spacing(0.5, 2) // 4px 16px
    },
    // size='large'
    containedSizeLarge: {
      ...customTheme.typography.buttonLarge,
      height: customTheme.spacing(6.25), // 50px
      padding: customTheme.spacing(2, 5) // 16px 40px
    },
    // color='primary'
    containedPrimary: {
      backgroundColor: customTheme.palette.primary.main,
      color: customTheme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: lighten(customTheme.palette.primary.main, 0.1),
        boxShadow: `0px 4px 5px ${lighten(customTheme.palette.primary.main, 0.5)}`
      },
      '&:active': {
        backgroundColor: customTheme.palette.primary.main,
        boxShadow: `0px 4px 5px ${customTheme.palette.primary.light}`
      }
    },
    // color='secondary'
    containedSecondary: {
      color: customTheme.palette.common.white,
      backgroundColor: customTheme.palette.success.main,
      '&:hover': {
        backgroundColor: customTheme.palette.success.light
      }
    },
    outlined: {
      ...customTheme.typography.buttonSmall,
      border: `1px solid ${customTheme.palette.grey[300]}`,
      borderRadius: customTheme.shape.borderRadius * 2.5,
      padding: customTheme.spacing(1.5, 3), // 12px 24px
      height: customTheme.spacing(5), // 40px
      textTransform: 'capitalize',
      boxShadow: 'none',
      '&$disabled': {
        borderColor: customTheme.palette.grey[200],
        color: customTheme.palette.grey[400],
        backgroundColor: customTheme.palette.background.paper
      },
      '&:hover': {
        backgroundColor: customTheme.palette.grey[100],
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
      }
    },
    outlinedSizeLarge: {
      ...customTheme.typography.buttonLarge,
      height: customTheme.spacing(6.25), // 50px
      padding: customTheme.spacing(2, 5) // 16px 40px
    },
    outlinedSizeSmall: {
      ...customTheme.typography.buttonSmall,
      height: customTheme.spacing(3.75), // 30px
      padding: customTheme.spacing(0.5, 2) // 4px 16px
    }
  },
  MuiButtonBase: {
    root: {
      outline: 'none !important',
      '& focus': {
        outline: 'none !important'
      }
    }
  },
  MuiList: {
    root: {
      outline: 'none',
      '& focus': {
        outline: 'none'
      }
    }
  },
  MuiTableCell: {
    head: {
      fontWeight: fontWeights.fontWeightBold
    },
    root: {
      borderBottom: `1px solid ${customTheme.palette.divider}`
    }
  },
  MuiListItemIcon: {
    root: {
      minWidth: customTheme.spacing(4)
    }
  },
  MuiDialogActions: {
    spacing: {
      paddingTop: customTheme.spacing(3),
      paddingBottom: customTheme.spacing(2),
      paddingLeft: customTheme.spacing(3),
      paddingRight: customTheme.spacing(3)
    }
  },
  MuiLinearProgress: {
    colorPrimary: {
      backgroundColor: customTheme.palette.grey[100]
    },
    root: {
      borderRadius: customTheme.shape.borderRadius * 5
    }
  },
  MuiTablePagination: {
    selectRoot: {
      marginRight: 0
    },
    toolbar: {
      paddingLeft: 0
    }
  },
  MuiPaper: {
    elevation1: {
      boxShadow: `
        0px 1px 3px 0px ${colors.shadow},
        0px 1px 1px 0px ${colors.shadow},
        0px 2px 1px -1px ${colors.shadow}
      `
    }
  },
  MuiLink: {
    underlineNone: {
      textDecoration: 'none',
      '&:hover, &:active, &:focus': {
        textDecoration: 'none'
      }
    }
  },
  MuiMenu: {
    paper: {
      borderRadius: customTheme.shape.borderRadius * 2.5
    },
    list: {
      paddingTop: customTheme.spacing(0.5),
      paddingBottom: customTheme.spacing(0.5)
    }
  },
  MuiMenuItem: {
    root: {
      maxHeight: customTheme.spacing(5),
      paddingTop: customTheme.spacing(1),
      paddingBottom: customTheme.spacing(1),
      marginTop: customTheme.spacing(0.5),
      marginBottom: customTheme.spacing(0.5),
      minHeight: customTheme.spacing(5),
      '&:hover': {
        color: customTheme.palette.primary.main
      }
    }
  },
  MuiSelect: {
    select: {
      '&:focus': {
        backgroundColor: customTheme.palette.grey[100]
      }
    }
  },
  MuiListItem: {
    root: {
      '&.Mui-focusVisible': {
        backgroundColor: customTheme.palette.grey[100]
      }
    }
  },
  MuiAlert: {
    message: {
      paddingTop: defaultTheme.spacing(0.75),
      paddingLeft: defaultTheme.spacing(1.5)
    },
    action: {
      marginRight: defaultTheme.spacing(0.5),
      paddingLeft: defaultTheme.spacing(1)
    },
    /* Success */
    standardSuccess: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.success.smoke,
      '& $icon': {
        color: customTheme.palette.success.main
      }
    },
    outlinedSuccess: {
      color: customTheme.palette.text.primary,
      border: `1px solid ${customTheme.palette.success.medium}`,
      backgroundColor: customTheme.palette.success.smoke,
      '& $icon': {
        color: customTheme.palette.success.main
      }
    },
    filledSuccess: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.success.smoke
    },
    /* Info */
    standardInfo: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.primary.smoke,
      '& $icon': {
        color: customTheme.palette.primary.main
      }
    },
    outlinedInfo: {
      color: customTheme.palette.text.primary,
      border: `1px solid ${customTheme.palette.primary.main}`,
      backgroundColor: customTheme.palette.primary.smoke,
      '& $icon': {
        color: customTheme.palette.primary.main
      }
    },
    filledInfo: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.primary.smoke
    },
    /* Warning */
    standardWarning: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.warning.smoke,
      '& $icon': {
        color: customTheme.palette.warning.main
      }
    },
    outlinedWarning: {
      color: customTheme.palette.text.primary,
      border: `1px solid ${customTheme.palette.warning.main}`,
      backgroundColor: customTheme.palette.warning.smoke,
      '& $icon': {
        color: customTheme.palette.warning.main
      }
    },
    filledWarning: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.warning.smoke
    },
    /* Error */
    standardError: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.error.smoke,
      '& $icon': {
        color: customTheme.palette.error.main
      }
    },
    outlinedError: {
      color: customTheme.palette.text.primary,
      border: `1px solid ${customTheme.palette.error.main}`,
      backgroundColor: customTheme.palette.error.smoke,
      '& $icon': {
        color: customTheme.palette.error.main
      }
    },
    filledError: {
      color: customTheme.palette.text.primary,
      backgroundColor: customTheme.palette.error.smoke
    }
  }
};

export default customTheme;
